import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useTodoActions from '@hooks/useTodoActions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import AvailabilityModal from '@components/Modal/AvailabilityModal';
import useAuth from '@vl-core/useAuth';
import { Spin } from 'antd';
import { RightOutlined } from 'antd/icons';
import styled from '@emotion/styled';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import VirtualOnlyLabel from './VirtualOnlyLabel';

const Heading = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  padding-bottom: 1rem;
`;

const EmptyTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
`;

const SelfReferHint = styled.div`
  font-size: 0.875rem;
  padding-top: 0.3125rem;
`;

const TodoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: max-content 1fr;
  column-gap: 0.75rem;
  row-gap: 0;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    column-gap: 0;
  }
`;

const CardWrapper = styled.div`
  display: grid;
  grid-row: span 2;
  grid-template-rows: subgrid;
  height: 100%;
  width: 100%;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    margin-bottom: 0.375rem;
  }
`;

const StepButton = styled.button`
  grid-row: 2;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0.875rem 1.25rem;
  border: 1px solid #8896a7;
  border-radius: 1rem;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 16px rgba(136, 150, 167, 0.35);
  }

  &:active {
    box-shadow: 0 2px 8px rgba(136, 150, 167, 0.25);
  }

  @media (max-width: 768px) {
    min-height: 3rem;
    border-radius: 0.5rem;
    padding: 0.625rem 0.75rem;
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    column-gap: 0.5rem;
  }
`;

const CardLabel = styled.span`
  font-size: 0.8125rem;
  color: #8896a7;
`;

const CardCondition = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: #555;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const StyledArrow = styled(RightOutlined)`
  color: #8896a7;
  font-size: 1rem;
  flex-shrink: 0;
  margin-left: 0.625rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const MoreStepsFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
`;

const MoreStepsLink = styled(Link)`
  font-size: 0.95rem;
  font-weight: 600;
  font-style: italic;
  text-decoration: underline;
  color: var(--productPrimary);

  &:active {
    color: var(--productPrimaryDark);
  }
`;

const GridVirtualOnlyLabel = styled(VirtualOnlyLabel)`
  grid-row: 1;
`;

interface TodoCardProps {
  actionItem: any;
  todoItem: any;
  onClick: () => void;
  tBE: (key: string) => string;
}

const TodoCard = ({ actionItem, todoItem, onClick, tBE }: TodoCardProps) => {
  if (!actionItem || !todoItem) return null;

  return (
    <CardWrapper>
      <GridVirtualOnlyLabel caseId={todoItem.case_id} />
      <StepButton
        data-testid={actionItem.test_id ? `step-button-${actionItem.test_id}` : 'step-button'}
        onClick={onClick}
      >
        <CardContent>
          <CardLabel>{tBE(actionItem.text)}</CardLabel>
          <CardCondition aria-hidden>{todoItem.condition}</CardCondition>
        </CardContent>
        <StyledArrow aria-hidden />
      </StepButton>
    </CardWrapper>
  );
};

interface NextStepsProps {
  newCase: number | null;
}

const NextSteps = ({ newCase }: NextStepsProps) => {
  const { t } = useTranslation('common');
  const { t: tBE } = useTranslation('commonBackendKeys');
  const dispatch = useDispatch();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [case_id, setCase_id] = useState<number | null>(null);
  const [request, setRequest] = useState(false);
  const [apptType, setApptType] = useState('');
  const {
    data: { todo } = { todo: [] },
    refetch,
    isLoading
  } = useQuery({
    queryKey: ['getNextSteps'],
    queryFn: async () => {
      const { todo } = await dispatch(actions.getIncompletedToDosList());
      return { todo: todo || [] };
    }
  });

  const { data: { surveyjs_defs } = { surveyjs_defs: [] } } = useQuery({
    queryKey: ['useSurvey'],
    queryFn: () => dispatch(actions.getSurveyJsDefinition({ cat_code: 'UI', event_code: 'SR', current: 'true' }))
  });
  const noSurveyDefinition = surveyjs_defs?.length > 0;

  const filteredTodo = useMemo(() => {
    let list = todo || [];
    if (!noSurveyDefinition) {
      list = list.filter((item) => item.todo_type !== 'nocase');
    }
    return list.filter((item) => item.todo_type !== 'explan' || Boolean(item.file_guid));
  }, [todo, noSurveyDefinition]);

  const canSelfRefer = user?.self_refer && user?.insured;

  useEffect(() => {
    if (!user?.restricted && user?.user_guid) {
      queryClient
        .invalidateQueries({
          queryKey: ['getGPDetails', user.user_guid]
        })
        .then();
    }
  }, []);

  const showAvailabilityModal = useCallback((caseId: number | null, apptType: string) => {
    setCase_id(caseId);
    setApptType(apptType);
    setRequest(true);
  }, []);

  const router = useRouter();

  const todosShown = useMemo(() => filteredTodo.slice(0, 4), [filteredTodo]);

  const todoActions = useTodoActions(todosShown, showAvailabilityModal, refetch);

  return (
    <>
      <Heading>{t("What's next")}:</Heading>
      {todo?.length === 0 ? (
        <div>
          <EmptyTitle>{t('You currently have no next steps')}</EmptyTitle>
          {canSelfRefer && <SelfReferHint>{t('Tell us about your condition using the button above.')}</SelfReferHint>}
        </div>
      ) : (
        <div>
          <Spin spinning={isLoading} style={{ width: '100%' }}>
            <TodoGrid>
              {todoActions.map((item, index) => {
                const underlyingTodo = todosShown[index] || todo[index];

                const caseId = underlyingTodo?.case_id || 'no-case';
                const actionId = item?.test_id || index;
                const compositeKey = `todo-card-${caseId}-${actionId}`;

                return (
                  <TodoCard
                    key={compositeKey}
                    actionItem={item}
                    todoItem={underlyingTodo}
                    tBE={tBE}
                    onClick={
                      todoActions[index]?.action ||
                      (() => {
                        sessionStorage.setItem('case-filter-selected', String(underlyingTodo?.case_id));
                        router.push('/patients/todos/').then();
                      })
                    }
                  />
                );
              })}
            </TodoGrid>
          </Spin>

          <MoreStepsFooter>
            <MoreStepsLink href="/patients/todos" data-testid="more-steps">
              {t('More steps…')}
            </MoreStepsLink>
          </MoreStepsFooter>
        </div>
      )}

      <AvailabilityModal
        open={request}
        setRequest={setRequest}
        user={user}
        case_id={case_id}
        apptType={apptType}
        updateActiveList={refetch}
      />
    </>
  );
};

export default NextSteps;
